import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { CreditCardType } from '@platform-frontend/payment-util';
import { PLATFORM_ENVIRONMENT, PollingConfiguration } from '@platform-frontend/platform';
import {
  ClearNotifications,
  convertToHttpParams,
  handleErrors,
  InvalidStateError,
  PlannedError,
  ShowErrorNotification,
  tapNextOrError
} from '@platform-frontend/util';
import { Observable, Subject, throwError } from 'rxjs';
import { map, tap, timeout } from 'rxjs/operators';
import { MSG_CHECK_SCA_VERIFICATION_DEFAULT } from '../notification-messages';
import { MARKETPLACE_ERROR_HANDLERS } from './../marketplace-error-handlers';
import { PaymentMethodRedirection } from './model/payment-method-redirection';
import { ScaReason } from './model/sca-reason';
import { ScaRedirection } from './model/sca-redirection';
import { ScaVerification } from './model/sca-verification';
import { GotoScaSpinner } from './sca.actions';

@Injectable({
  providedIn: 'root'
})
export class ScaService {
  constructor(private http: HttpClient, private store: Store) {}

  private static readonly POLLING_TAG = Symbol('ScaService: PollingTag');
  private redirect: ScaRedirection | null = null;
  private result: Subject<void> = new Subject();

  performSca(redirection: PaymentMethodRedirection, cardType: CreditCardType, reason: ScaReason): Observable<unknown> {
    this.redirect = {
      cardType: cardType,
      redirectId: redirection.redirectId,
      reason,
      url: redirection.url
    };
    this.result = new Subject<void>();
    this.store.dispatch(new GotoScaSpinner());
    return this.result;
  }

  getPollingConfiguration(): PollingConfiguration {
    return {
      poll: timeout => this.checkVerification(timeout)
    };
  }

  private checkVerification(timeoutInMilliseconds: number): Observable<boolean> {
    const { redirectId } = this.getRedirect();
    const params = convertToHttpParams({ redirectId });

    return this.http.get<ScaVerification>(PLATFORM_ENVIRONMENT.apiUrl + '/purchase/authentication', { params }).pipe(
      timeout({ each: timeoutInMilliseconds - 50, with: () => throwError(() => new PlannedError('SERVER_UNREACHABLE')) }),
      tapNextOrError(() => this.store.dispatch(new ClearNotifications(ScaService.POLLING_TAG))),
      tap(response => this.completeIfVerified(response)),
      map(response => response.verified),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        DEFAULT: new ShowErrorNotification(MSG_CHECK_SCA_VERIFICATION_DEFAULT, { tags: [ScaService.POLLING_TAG] }),
        SERVER_UNREACHABLE: new ShowErrorNotification(MSG_CHECK_SCA_VERIFICATION_DEFAULT, { tags: [ScaService.POLLING_TAG] }),
        REDIRECT_ID_NOT_KNOWN: () => this.cancel('REDIRECT_ID_NOT_KNOWN'),
        AUTHORIZATION_REFUSED: () => this.cancel('AUTHORIZATION_REFUSED'),
        CANCELED_BY_USER: () => this.cancel('CANCELED_BY_USER')
      })
    );
  }

  private completeIfVerified(response: ScaVerification): void {
    if (response.verified) {
      this.redirect = null;
      this.result.next(undefined);
      this.result.complete();
    }
  }

  cancel(errorType: string = 'CANCELED_BY_USER'): void {
    const { redirectId } = this.getRedirect();
    this.redirect = null;
    this.http.put<void>(PLATFORM_ENVIRONMENT.apiUrl + '/purchase/authentication/cancel', { redirectId }).subscribe();
    this.store.dispatch(new ClearNotifications(ScaService.POLLING_TAG));
    this.result.error(new PlannedError(errorType));
  }

  isInProgress(): boolean {
    return this.redirect !== null;
  }

  getCardType(): CreditCardType {
    return this.getRedirect().cardType;
  }

  getReason(): ScaReason {
    return this.getRedirect().reason;
  }

  getUrl(): string {
    return this.getRedirect().url;
  }

  private getRedirect(): ScaRedirection {
    if (this.redirect === null) {
      throw new InvalidStateError('SCA service not initialized');
    }
    return this.redirect;
  }

  getResult(): Observable<void> {
    return this.result;
  }
}
